import { Navigate } from 'react-router-dom';
import Loader from '@/app/routes/Loader';
import ErrorPage403 from '@/app/routes/errorPage403';
import InlineLayout from '@/shared/layout/inlineLayout';
import { useViewSubmittedForm } from '@epics/taskFormManagement/hooks/useViewSubmittedForm';
import { VIEW_SUBMITTED_FORM_LABELS as L } from '@epics/taskFormManagement/constants/labels';
import BasicDetailsTab from './BasicDetailsTab';
import TaskReportTab from './TaskReportTab';

const NAV_ITEMS = [
  { id: 'basic', label: L.TAB_BASIC_DETAILS },
  { id: 'task', label: L.TAB_TASK_REPORT },
] as const;

interface Props {
  submittedFormId: string;
}

export default function ViewSubmittedForm({ submittedFormId }: Props) {
  const {
    data,
    isLoading,
    error,
    notFound,
    forbidden,
    clientSignatureUrl,
    techSignatureUrl,
    attachmentUrls,
  } = useViewSubmittedForm(submittedFormId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (notFound) {
    return <Navigate to="/404" replace />;
  }

  if (forbidden) {
    return <ErrorPage403 />;
  }

  if (error || !data) {
    return (
      <div className="w-full h-full flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4">
        <p className="text-sm text-error">{error ?? L.LOADING_ERROR}</p>
      </div>
    );
  }

  const formIdHeader = (
    <p className="text-base font-medium text-neutral-10">
      {data.task_form.form_id}
    </p>
  );

  return (
    <InlineLayout
      title={L.PAGE_TITLE}
      header={formIdHeader}
      navItems={[...NAV_ITEMS]}
      searchParamKey="tab"
      defaultActiveId="basic"
      contentByNavId={{
        basic: <BasicDetailsTab data={data} />,
        task: (
          <TaskReportTab
            data={data}
            clientSignatureUrl={clientSignatureUrl}
            techSignatureUrl={techSignatureUrl}
            attachmentUrls={attachmentUrls}
          />
        ),
      }}
    />
  );
}
